import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import {
  CURRENT_SOCKET_ID_VERSION,
  SOCKET_ID_VERSION_CONFIG_KEY,
  socketIdVersion,
} from "../../utils/dynamicSockets/dynamicSocketId";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import { eligibleEntityIdsSchema } from "./eligible-entity-ids";

/**
 * Output keys reserved by the step itself; user-defined supplier routes
 * cannot collide with these under socket-id version 1.
 */
export const IDENTIFY_SUPPLIER_RESERVED_SOCKET_IDS = [
  "top_pick",
  "others",
  "matched_supplier_id",
  "matched_supplier_name",
  "confidence_band",
  "match_score",
  "matching_reasoning",
  "closest_candidate_supplier_id",
  "closest_candidate_supplier_name",
] as const;

const supplierRouteSchema = z.object({
  id: z
    .string()
    .regex(
      /^[a-z][a-z0-9_]*$/,
      "Route id must be lowercase alphanumeric with underscores (valid socket id)",
    )
    .describe(
      "Output socket id for this supplier route. Lowercase snake_case.",
    ),
  supplierId: z
    .uuid()
    .describe("Supplier to route to this socket when matched."),
  label: z
    .string()
    .optional()
    .describe("Optional display label for the socket."),
});

/**
 * Identify Supplier Step
 *
 * Resolves the vendor behind an email or document using supplier-native
 * matching (heuristic tax-id / name / alias / email evidence, then vector
 * search, then closed-set LLM over the remaining catalog) and exposes the
 * result through dynamic per-supplier output sockets.
 *
 * Unlike identify_customer (Pinecone + pg_trgm over a large buyer catalog),
 * suppliers are a smaller closed set with stronger identity signals (EIN/tax
 * id, remit-to aliases, sender-domain corroboration). The handler wraps
 * EnhancedSupplierMatchingService so AP invoice automation and this node
 * share one matcher.
 *
 * `eligibleSupplierIds` restricts the catalog the matcher is allowed to
 * consider — the rollout knob for deploying a workflow to a handful of
 * vendors first.
 */
export const identifySupplierStep = defineStepConfig({
  type: "identify_supplier",
  name: "Identify Supplier",
  description:
    "Identify the supplier behind an email or document using tax ID, name/alias, and email evidence, then route by matched supplier",
  category: "AI & Agents",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL)
      .optional()
      .describe(
        "Inbound email to use for matching. Attachments and From/To/Subject/Body context are pulled in automatically.",
      ),
    documents: z
      .array(entityOfType(SystemEntity.CORE_DOCUMENT))
      .optional()
      .describe(
        "Documents to use for identifier extraction. Signed URLs are fetched on the backend.",
      ),
    preExtractedIdentifiers: z
      .object({
        possibleNames: z.array(z.string()).optional(),
        emailDomains: z.array(z.string()).optional(),
        emailAddresses: z.array(z.string()).optional(),
        taxId: z.string().nullable().optional(),
      })
      .optional()
      .describe(
        "Pre-extracted supplier identifiers from an upstream step. Skips the AI extraction phase when provided.",
      ),
    additionalContext: z
      .string()
      .optional()
      .describe(
        "Free-text context appended to whatever inboundEmail/documents context the handler builds.",
      ),
  }),

  outputSchema: z.record(z.string(), z.any()),

  configSchema: z
    .object({
      minPickConfidenceScore: z
        .number()
        .min(0)
        .max(1)
        .default(0.6)
        .describe(
          "Minimum 0–1 match score required to surface a top pick. Heuristic auto-accepts (tax ID / corroborated name) always surface.",
        ),
      useLLMDisambiguation: z
        .boolean()
        .default(true)
        .describe(
          "When true (default), run LLM arbitration / closed-set fallback when heuristics and vector search are not decisive.",
        ),
      additionalInstructions: z
        .string()
        .optional()
        .describe(
          "Extra instructions appended to identifier extraction and the matcher prompt.",
        ),
      eligibleSupplierIds: eligibleEntityIdsSchema,
      supplierRoutes: z
        .array(supplierRouteSchema)
        .default([])
        .superRefine((routes, ctx) => {
          const seen = new Set<string>();
          routes.forEach((route, index) => {
            if (seen.has(route.id)) {
              ctx.addIssue({
                code: "custom",
                message: `Duplicate supplierRoutes[].id: ${route.id}`,
                path: [index, "id"],
              });
              return;
            }
            seen.add(route.id);
          });
        })
        .describe(
          "Per-supplier output sockets. Each route fires when the matched supplier's id equals route.supplierId.",
        ),
      enableOthersRoute: z
        .boolean()
        .default(false)
        .describe(
          "When true, an `others` socket is emitted for matches that don't match any configured route.",
        ),
      othersEmitNullWhenNoMatch: z
        .boolean()
        .default(false)
        .describe(
          "When enableOthersRoute is true: if true and identification produces no surfaced supplier, `others` emits null so downstream steps on that wire still run.",
        ),
      [SOCKET_ID_VERSION_CONFIG_KEY]: z
        .number()
        .int()
        .optional()
        .describe(
          "Socket-id scheme this step was created under. Absent means the legacy scheme, where a route's name IS its output socket id.",
        ),
    })
    .superRefine((config, ctx) => {
      if (socketIdVersion(config) >= CURRENT_SOCKET_ID_VERSION) return;

      config.supplierRoutes?.forEach((route, index) => {
        if (
          (IDENTIFY_SUPPLIER_RESERVED_SOCKET_IDS as readonly string[]).includes(
            route.id,
          )
        ) {
          ctx.addIssue({
            code: "custom",
            message: `Route id cannot be one of the reserved socket ids: ${IDENTIFY_SUPPLIER_RESERVED_SOCKET_IDS.join(", ")}`,
            path: ["supplierRoutes", index, "id"],
          });
        }
      });
    }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },

  evalConfig: {
    evalOutputKeys: ["matched_supplier_id"],
    outputFormat: "object",
    contextOutputKeys: [
      "matched_supplier_name",
      "confidence_band",
      "match_score",
      "matching_reasoning",
      "closest_candidate_supplier_id",
      "closest_candidate_supplier_name",
    ],
  },
});
